import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "./AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { Section, TextField, TextArea, Toggle, AdminButton } from "@/components/admin/AdminUI";
import WeddingGalleryManager from "@/components/admin/WeddingGalleryManager";
import { Pencil, Trash2, Plus, Image as ImageIcon, Film } from "lucide-react";

/**
 * WeddingsAdmin — gallery-first admin panel.
 *
 * Flow:
 *  - "New Wedding" immediately creates a draft on the server (with a
 *    placeholder name) so a stable wedding_id exists. Uploads are then
 *    auto-associated. If the user Cancels without editing anything real,
 *    the draft is deleted.
 *  - Editing an existing wedding opens the same modal with all sections
 *    active. Photo/video uploads persist immediately via the gallery
 *    manager. Details (name, location, cover, order) are persisted on Save.
 */

const emptyDetails = {
  name: "", location: "", mood_line: "", description: "",
  featured: false, display_order: 0,
  cover_media_id: null, cover_url: "", cover_public_id: null, cover_storage_key: null,
};

const DRAFT_PLACEHOLDER_NAME = "Untitled wedding";

export default function WeddingsAdmin() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // {id, ...details, isDraft, hasEdited}
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");
  const creatingRef = React.useRef(false);

  const load = useCallback(async () => {
    try {
      try { await api.post("/weddings/sweep-drafts"); } catch (_) {}
      const r = await api.get("/weddings", { params: { include_drafts: true } });
      setList(r.data || []);
    } catch (_) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  /* -------------------------- CREATE (draft) --------------------------- */
  const startNew = async () => {
    if (creatingRef.current) return;
    creatingRef.current = true;
    setCreating(true); setErr("");
    try {
      const { data } = await api.post("/weddings", {
        name: DRAFT_PLACEHOLDER_NAME,
        location: "",
      });
      setEditing({
        ...emptyDetails,
        id: data.id, slug: data.slug,
        name: "", // clear placeholder — user must type a real name
        isDraft: true, hasEdited: false,
      });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  const startEdit = (w) => {
    setEditing({
      ...emptyDetails,
      id: w.id,
      slug: w.slug,
      name: w.name || "",
      location: w.location || "",
      mood_line: w.mood_line || "",
      description: w.description || "",
      featured: !!w.featured,
      display_order: w.display_order || 0,
      cover_media_id: w.cover_media_id || w.cover?.media_id || null,
      cover_url: w.cover_url || "",
      cover_public_id: w.cover_public_id || null,
      cover_storage_key: w.cover_storage_key || null,
      isDraft: false, hasEdited: false,
    });
  };

  /* --------------------------- SAVE / DELETE --------------------------- */
  const setField = (patch) => setEditing((prev) => prev ? { ...prev, ...patch, hasEdited: true } : prev);

  const onCoverChange = (mediaId, mediaObj) => {
    setEditing((prev) => prev ? ({
      ...prev,
      hasEdited: true,
      cover_media_id: mediaId,
      cover_url: mediaObj?.url || "",
      cover_public_id: mediaObj?.public_id || null,
      cover_storage_key: mediaObj?.storage_key || null,
    }) : prev);
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!editing) return;
    if (!editing.name.trim() || !editing.location.trim()) {
      setErr("Name and location are required.");
      return;
    }
    setSaving(true); setErr("");
    try {
      const body = {
        name: editing.name.trim(),
        location: editing.location.trim(),
        mood_line: editing.mood_line || "",
        description: editing.description || "",
        featured: !!editing.featured,
        display_order: Number(editing.display_order) || 0,
        cover_media_id: editing.cover_media_id || null,
        cover_url: editing.cover_url || "",
        cover_public_id: editing.cover_public_id || null,
        cover_storage_key: editing.cover_storage_key || null,
        // Do NOT send photos/videos — they are managed live by the gallery.
        photos: [],
        videos: [],
      };
      await api.put(`/weddings/${editing.id}`, body);
      setEditing(null);
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    if (!editing) return;
    // Draft cleanup: if this was auto-created and user didn't set a real name, drop it.
    if (editing.isDraft && !editing.hasEdited) {
      try { await api.delete(`/weddings/${editing.id}`); } catch (_) {}
    }
    setEditing(null);
    await load();
  };

  const del = async (w) => {
    if (!window.confirm(`Delete "${w.name}"? This removes all its photos and videos from S3.`)) return;
    try {
      // Delete all media associated with this wedding first (DB + S3)
      const { data } = await api.get("/media", { params: { wedding_id: w.id, limit: 500 } });
      const ids = (data.items || []).map((m) => m.id);
      if (ids.length) {
        // chunk into 500 max (API-enforced)
        for (let i = 0; i < ids.length; i += 500) {
          await api.post("/media/bulk", { ids: ids.slice(i, i + 500), action: "delete" });
        }
      }
      await api.delete(`/weddings/${w.id}`);
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <AdminLayout>
      <Section
        title="Weddings"
        actions={
          <AdminButton testId="new-wedding" onClick={startNew} disabled={creating}>
            <Plus size={14} /> {creating ? "Creating…" : "New Wedding"}
          </AdminButton>
        }
      >
        {err && !editing && <div className="text-red-400 text-sm mb-4">{err}</div>}
        <div className="border border-[color:var(--border)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--surface)]">
              <tr className="text-left eyebrow text-[color:var(--text-muted)]">
                <th className="p-4">Cover</th>
                <th className="p-4">Name</th>
                <th className="p-4 hidden md:table-cell">Location</th>
                <th className="p-4">Media</th>
                <th className="p-4 hidden md:table-cell">Order</th>
                <th className="p-4">Featured</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((w) => (
                <tr key={w.id} data-testid={`row-wedding-${w.id}`} className="border-t border-[color:var(--border)]">
                  <td className="p-4">
                    {w.cover_url ? (
                      <img src={w.cover_url} alt="" className="w-16 h-16 object-cover" loading="lazy" />
                    ) : (
                      <div className="w-16 h-16 bg-[color:var(--surface)] flex items-center justify-center text-[color:var(--text-muted)] text-[10px] uppercase tracking-wider">
                        No cover
                      </div>
                    )}
                  </td>
                  <td className="p-4 font-display italic text-lg">
                    {w.name === DRAFT_PLACEHOLDER_NAME || !(w.name || "").trim() ? (
                      <span className="text-[color:var(--text-muted)]">
                        Untitled wedding
                        <span className="ml-2 inline-block px-1.5 py-0.5 border border-[color:var(--accent)] text-[color:var(--accent)] text-[9px] tracking-widest align-middle not-italic font-sans">
                          DRAFT
                        </span>
                      </span>
                    ) : (
                      w.name
                    )}
                  </td>
                  <td className="p-4 hidden md:table-cell text-[color:var(--text-muted)]">{w.location}</td>
                  <td className="p-4 text-[color:var(--text-muted)] whitespace-nowrap">
                    <span className="inline-flex items-center gap-1" data-testid={`wedding-photo-count-${w.id}`}>
                      <ImageIcon size={12} /> {(w.photos || []).length}
                    </span>
                    <span className="mx-2 opacity-30">·</span>
                    <span className="inline-flex items-center gap-1" data-testid={`wedding-video-count-${w.id}`}>
                      <Film size={12} /> {(w.videos || []).length}
                    </span>
                  </td>
                  <td className="p-4 hidden md:table-cell">{w.display_order}</td>
                  <td className="p-4">{w.featured ? "★" : ""}</td>
                  <td className="p-4 text-right whitespace-nowrap">
                    <button data-testid={`edit-wedding-${w.id}`} onClick={() => startEdit(w)} className="mr-3 text-[color:var(--text-muted)] hover:text-[color:var(--accent)]"><Pencil size={16} /></button>
                    <button data-testid={`delete-wedding-${w.id}`} onClick={() => del(w)} className="text-[color:var(--text-muted)] hover:text-red-400"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr>
                  <td colSpan="7" className="p-10 text-center text-[color:var(--text-muted)]">
                    No weddings yet — click <span className="text-[color:var(--accent)]">New Wedding</span> to start.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto p-4 md:p-10"
          onClick={(e) => e.target === e.currentTarget && cancel()}
        >
          <form
            onSubmit={save}
            className="max-w-5xl mx-auto bg-[color:var(--surface)] border border-[color:var(--border)] p-6 md:p-10"
          >
            <div className="flex items-start justify-between mb-6 gap-4">
              <div>
                <div className="eyebrow text-[color:var(--text-muted)]">
                  {editing.isDraft ? "New Wedding (draft)" : `Editing · slug: ${editing.slug}`}
                </div>
                <h3 className="font-display italic text-3xl mt-1">
                  {editing.name || "Untitled wedding"}
                </h3>
              </div>
            </div>

            {/* Details section */}
            <div className="mb-8 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField testId="wedding-name" label="Name *" value={editing.name}
                           onChange={(v) => setField({ name: v })} />
                <TextField testId="wedding-location" label="Location *" value={editing.location}
                           onChange={(v) => setField({ location: v })} />
              </div>
              <TextField testId="wedding-mood" label="Mood line" value={editing.mood_line}
                         onChange={(v) => setField({ mood_line: v })} />
              <TextArea testId="wedding-description" label="Description" rows={4} value={editing.description}
                        onChange={(v) => setField({ description: v })} />
              <div className="grid grid-cols-2 gap-6">
                <TextField testId="wedding-order" label="Display order" type="number" value={editing.display_order}
                           onChange={(v) => setField({ display_order: parseInt(v) || 0 })} />
                <Toggle testId="wedding-featured" label="Featured on homepage" value={editing.featured}
                        onChange={(v) => setField({ featured: v })} />
              </div>
            </div>

            {/* Current cover preview */}
            <div className="mb-8">
              <div className="eyebrow mb-3">Cover</div>
              {editing.cover_url ? (
                <div className="flex items-center gap-4">
                  <img src={editing.cover_url} alt="" className="w-32 h-32 object-cover border border-[color:var(--border)]" />
                  <div className="text-sm text-[color:var(--text-muted)]">
                    Click the <span className="text-[color:var(--accent)]">★</span> on any photo in the gallery below to change the cover.
                  </div>
                </div>
              ) : (
                <div className="border border-dashed border-[color:var(--border)] p-6 text-sm text-[color:var(--text-muted)]">
                  No cover selected. Upload a photo below and click the <span className="text-[color:var(--accent)]">★</span> icon on it to set as cover.
                </div>
              )}
            </div>

            {/* Gallery manager */}
            <div className="mb-8">
              <div className="eyebrow mb-3">Gallery</div>
              <WeddingGalleryManager
                weddingId={editing.id}
                weddingSlug={editing.slug}
                currentCoverMediaId={editing.cover_media_id}
                onCoverChange={onCoverChange}
                onCountsChange={() => { /* counts are re-fetched by list on save */ }}
                onDidChange={() => setEditing((prev) => prev ? { ...prev, hasEdited: true } : prev)}
              />
            </div>

            {err && <div className="text-red-400 text-sm mb-4" data-testid="wedding-form-error">{err}</div>}
            <div className="flex justify-between items-center gap-3 pt-4 border-t border-[color:var(--border)]">
              <div className="text-xs text-[color:var(--text-muted)]">
                Media uploads save automatically. Click <span className="text-[color:var(--text)]">Save details</span> to persist name, location, cover choice and order.
              </div>
              <div className="flex gap-3">
                <AdminButton variant="ghost" type="button" onClick={cancel} testId="wedding-cancel">
                  {editing.isDraft && !editing.hasEdited ? "Discard draft" : "Close"}
                </AdminButton>
                <AdminButton type="submit" testId="wedding-save" disabled={saving}>
                  {saving ? "Saving…" : "Save details"}
                </AdminButton>
              </div>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
